import { PersonModel } from '@_types/molecules';
import Button from '@atoms/buttons/Button';
import PhoneRevealButton from '@atoms/buttons/PhoneRevealButton';
import Link from '@atoms/links/Link';
import Picture from '@atoms/pictures/Picture';
import { PhoneClickButtonType, PhoneClickItemPlacement } from '@helpers/phoneClickTracking';
import { isPhoneRevealDataSet } from '@helpers/phoneReveal';
import { getModifiers } from '@helpers/functions';

const parseTracking = (dataSet?: { [key: string]: string | undefined | boolean }) => {
  let buttonType: PhoneClickButtonType = 'tertiary';
  let itemPlacement: PhoneClickItemPlacement | undefined;

  try {
    const tracking = dataSet?.['data-phone-click'] ? JSON.parse(String(dataSet['data-phone-click'])) : null;

    if (tracking?.button_type) buttonType = tracking.button_type;
    if (tracking?.item_placement) itemPlacement = tracking.item_placement;
  } catch {
    // ignore invalid tracking payload
  }

  return { buttonType, itemPlacement };
};

const withButtonType = (
  dataSet: { [key: string]: string | undefined | boolean } | undefined,
  buttonType: PhoneClickButtonType,
  itemPlacement?: PhoneClickItemPlacement
) => {
  if (!itemPlacement) return dataSet;

  return {
    ...dataSet,
    'data-phone-click': JSON.stringify({
      button_type: buttonType,
      item_placement: itemPlacement,
    }),
  };
};

export default function ContactFormPerson(model: PersonModel) {
  const { image, name, refNo, contactLinks } = model;
  const styleModifiers = getModifiers(model, 'kf-m-contact-form-person');
  const phoneLinks = contactLinks?.filter((link) => !link.className) ?? [];
  const hasRevealLinks = phoneLinks.some((link) => isPhoneRevealDataSet(link.dataSet));

  const renderDesktopRevealLink = (link: (typeof phoneLinks)[number], index: number) => {
    const { className, dataSet, text, url } = link;
    const phoneNumber = String(dataSet?.['data-phone-number'] ?? '');
    const phoneHref = String(dataSet?.['data-phone-href'] ?? url ?? '#');
    const { itemPlacement } = parseTracking(dataSet);
    const buttonType: PhoneClickButtonType = 'tertiary';

    return (
      <div key={index} className={`kf-m-contact-form-person__info__link ${className ?? ''}`}>
        <PhoneRevealButton
          label={text || 'Call'}
          phoneNumber={phoneNumber}
          phoneHref={phoneHref}
          buttonType={buttonType}
          itemPlacement={itemPlacement}
          id={itemPlacement ? `phone-reveal-${itemPlacement}-desktop-${index}` : undefined}
          showIcon
          styleModifier={['tertiary', 'large']}
          dataSet={withButtonType(dataSet, buttonType, itemPlacement)}
        />
      </div>
    );
  };

  const renderMobileRevealButton = (link: (typeof phoneLinks)[number], index: number) => {
    const { className, dataSet, text, url } = link;
    const phoneNumber = String(dataSet?.['data-phone-number'] ?? '');
    const phoneHref = String(dataSet?.['data-phone-href'] ?? url ?? '#');
    const { itemPlacement } = parseTracking(dataSet);
    const buttonType: PhoneClickButtonType = 'secondary';

    return (
      <div key={index} className={`kf-m-contact-form-person__bottom__call ${className ?? ''}`}>
        <PhoneRevealButton
          label={text || 'Call'}
          phoneNumber={phoneNumber}
          phoneHref={phoneHref}
          buttonType={buttonType}
          itemPlacement={itemPlacement}
          id={itemPlacement ? `phone-reveal-${itemPlacement}-mobile-${index}` : undefined}
          showIcon
          styleModifier={['secondary', 'full-width']}
          dataSet={withButtonType(dataSet, buttonType, itemPlacement)}
        />
      </div>
    );
  };

  return (
    <div className={`${styleModifiers}${hasRevealLinks ? ' kf-m-contact-form-person--reveal' : ''}`}>
      <div className="kf-m-contact-form-person__top">
        {image ? (
          <div className="kf-m-contact-form-person__image">
            <Picture {...image} />
          </div>
        ) : null}
        <div className="kf-m-contact-form-person__info">
          {name ? <div className="kf-m-contact-form-person__info__name">{name}</div> : null}
          {refNo && <div className="kf-m-contact-form-person__info__ref-no">Ref No: {refNo}</div>}
          {hasRevealLinks ? (
            <div className="kf-m-contact-form-person__info__links kf-m-contact-form-person__info__links--reveal">
              {phoneLinks.map((link, index) => renderDesktopRevealLink(link, index))}
            </div>
          ) : (
            <div className="kf-m-contact-form-person__info__links">
              {phoneLinks.map((link, index) => {
                const { className, ...rest } = link;

                return (
                  <div key={index} className={`kf-m-contact-form-person__info__link ${className ?? ''}`}>
                    <Link {...rest} styleModifier={['red']} />
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>
      {hasRevealLinks ? (
        <div className="kf-m-contact-form-person__bottom kf-m-contact-form-person__bottom--reveal">
          {phoneLinks.map((link, index) => renderMobileRevealButton(link, index))}
        </div>
      ) : (
        <div className="kf-m-contact-form-person__bottom">
          {phoneLinks.map((link, index) => {
            const { className, dataSet, text, url } = link;

            if (phoneLinks.length === 1) {
              return (
                <div key={index} className={`kf-m-contact-form-person__bottom__call ${className ?? ''}`}>
                  <Button link dataSet={dataSet} href={url} styleModifier={['secondary', 'full-width']} text={'Call'} />
                </div>
              );
            }

            return (
              <div key={index} className={`kf-m-contact-form-person__bottom__call ${className ?? ''}`}>
                <Button link dataSet={dataSet} href={url} styleModifier={['secondary', 'full-width']} text={text} />
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
